import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../config';
import { TicketListSkeleton } from '../components/MatchCardSkeleton';
import './MyBetsPage.css';

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const isToday =
    d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Auj. ${time}`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + ` ${time}`;
}

function TeamImg({ src, alt, size = 28 }) {
  const [broken, setBroken] = React.useState(false);
  if (!src || broken) {
    return (
      <div
        className="mbt-team-fallback"
        style={{ width: size, height: size, fontSize: size * 0.55 }}
        title={alt}
      >⚽</div>
    );
  }
  return (
    <img
      src={src} alt={alt}
      width={size} height={size}
      className="mbt-team-img"
      onError={() => setBroken(true)}
    />
  );
}

const SELECTION_LABEL = { Home: '1', Draw: 'X', Away: '2' };

function itemStatusIcon(status) {
  if (status === 'won')  return '✅';
  if (status === 'lost') return '❌';
  return '⏳';
}

/* ─────────────────────────────────────────────
   Single item row inside a ticket
───────────────────────────────────────────── */
function BetItem({ item }) {
  return (
    <div className={`mbt-item mbt-item-${item.status}`}>
      <div className="mbt-item-teams">
        <TeamImg src={item.home_logo} alt={item.home_team} />
        <div className="mbt-item-teams-names">
          <span className="mbt-item-league">{item.league}</span>
          <span className="mbt-item-vs">
            <strong>{item.home_team}</strong>
            <span className="mbt-item-vs-sep">vs</span>
            <strong>{item.away_team}</strong>
          </span>
          <span className="mbt-item-date">{formatDate(item.match_date)}</span>
        </div>
        <TeamImg src={item.away_logo} alt={item.away_team} />
      </div>
      <div className="mbt-item-right">
        <span className="mbt-item-selection">
          {SELECTION_LABEL[item.selection] ?? item.selection}
        </span>
        <span className="mbt-item-odd">{parseFloat(item.odd_value).toFixed(2)}</span>
        <span className="mbt-item-status-icon">{itemStatusIcon(item.status)}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Ticket card (list view)
───────────────────────────────────────────── */
function TicketCard({ ticket, onSelect }) {
  const stake      = parseFloat(ticket.stake);
  const potWin     = parseFloat(ticket.potential_win);
  const totalOdd   = parseFloat(ticket.total_odd);
  const isCombo    = ticket.items?.length > 1;

  return (
    <div
      className={`mbt-ticket mbt-ticket-${ticket.status}`}
      onClick={() => onSelect(ticket)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(ticket)}
    >
      {/* Header */}
      <div className="mbt-ticket-header">
        <div className="mbt-ticket-meta">
          <span className={`mbt-ticket-badge mbt-badge-${ticket.status}`}>
            {ticket.status === 'won'     ? '✅ Gagné'      :
             ticket.status === 'lost'    ? '❌ Perdu'      :
             '⏳ En cours'}
          </span>
          <span className="mbt-ticket-type">{isCombo ? '🔗 Combiné' : '🎯 Simple'}</span>
          <span className="mbt-ticket-date">{formatDate(ticket.created_at)}</span>
        </div>
        <div className="mbt-ticket-odds">
          <span className="mbt-ticket-odd-label">Cote totale</span>
          <span className="mbt-ticket-odd-value">{totalOdd.toFixed(2)}</span>
        </div>
      </div>

      {/* Items preview (first 2, rest collapsed) */}
      <div className="mbt-ticket-items">
        {ticket.items?.slice(0, 2).map((item) => (
          <BetItem key={item.id} item={item} />
        ))}
        {ticket.items?.length > 2 && (
          <p className="mbt-ticket-more">+{ticket.items.length - 2} autre(s) match(s)</p>
        )}
      </div>

      {/* Footer */}
      <div className="mbt-ticket-footer">
        <div className="mbt-ticket-stake">
          <span className="mbt-footer-label">Mise</span>
          <span className="mbt-footer-value">{stake.toFixed(2)} FC</span>
        </div>
        <div className="mbt-ticket-win">
          {ticket.status === 'won' && (
            <>
              <span className="mbt-footer-label">Gain</span>
              <span className="mbt-footer-value green">+{potWin.toFixed(2)} FC</span>
            </>
          )}
          {ticket.status === 'lost' && (
            <>
              <span className="mbt-footer-label">Perdu</span>
              <span className="mbt-footer-value red">-{stake.toFixed(2)} FC</span>
            </>
          )}
          {ticket.status === 'pending' && (
            <>
              <span className="mbt-footer-label">Gain potentiel</span>
              <span className="mbt-footer-value accent">{potWin.toFixed(2)} FC</span>
            </>
          )}
        </div>
        <span className="mbt-ticket-detail-arrow">→</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Ticket detail drawer / modal
───────────────────────────────────────────── */
function TicketDetail({ ticket, onClose }) {
  const [detail, setDetail]   = useState(ticket);
  const [loading, setLoading] = useState(false);
  const { accessToken }       = useAuth();

  // Fetch full detail from API
  useEffect(() => {
    if (!ticket?.id || !accessToken) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/v1/bets/${ticket.id}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => r.ok ? r.json() : ticket)
      .then((data) => { setDetail(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [ticket, accessToken]);

  if (!detail) return null;

  const stake    = parseFloat(detail.stake);
  const potWin   = parseFloat(detail.potential_win);
  const totalOdd = parseFloat(detail.total_odd);

  return (
    <div className="mbt-overlay" onClick={onClose}>
      <div className="mbt-drawer" onClick={(e) => e.stopPropagation()}>
        <button className="mbt-drawer-close" onClick={onClose}>✕</button>

        <div className="mbt-drawer-header">
          <span className={`mbt-ticket-badge mbt-badge-${detail.status}`}>
            {detail.status === 'won'  ? '✅ Gagné'  :
             detail.status === 'lost' ? '❌ Perdu'  :
             '⏳ En cours'}
          </span>
          <p className="mbt-drawer-date">{formatDate(detail.created_at)}</p>
        </div>

        {/* Summary */}
        <div className="mbt-drawer-summary">
          <div className="mbt-ds-row">
            <span>Mise</span>
            <strong>{stake.toFixed(2)} FC</strong>
          </div>
          <div className="mbt-ds-row">
            <span>Cote totale</span>
            <strong>{totalOdd.toFixed(2)}</strong>
          </div>
          <div className="mbt-ds-row">
            <span>{detail.status === 'won' ? 'Gain' : 'Gain potentiel'}</span>
            <strong className={detail.status === 'won' ? 'green' : detail.status === 'lost' ? 'red' : 'accent'}>
              {detail.status === 'lost' ? `-${stake.toFixed(2)} FC` : `+${potWin.toFixed(2)} FC`}
            </strong>
          </div>
        </div>

        {/* All items */}
        {loading ? (
          <div className="mbt-drawer-loading">
            <span className="mbt-spinner" /> Chargement...
          </div>
        ) : (
          <div className="mbt-drawer-items">
            <p className="mbt-drawer-items-title">
              {detail.items?.length} sélection{detail.items?.length > 1 ? 's' : ''}
            </p>
            {detail.items?.map((item) => (
              <BetItem key={item.id} item={item} />
            ))}
          </div>
        )}

        {/* Ref */}
        <p className="mbt-drawer-ref">Réf. {detail.id}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
const TABS = [
  { label: '⏳ En cours', status: 'pending' },
  { label: '✅ Gagnés',   status: 'won'     },
  { label: '❌ Perdus',   status: 'lost'    },
];

export default function MyBetsPage() {
  const { accessToken } = useAuth();

  const [activeTab, setActiveTab]     = useState(0);
  const [tickets, setTickets]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [selected, setSelected]       = useState(null);

  const fetchBets = useCallback((status) => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    const url = `${API_BASE_URL}/api/v1/bets?status=${status}`;
    fetch(url, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then((data) => { setTickets(Array.isArray(data) ? data : []); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [accessToken]);

  useEffect(() => {
    fetchBets(TABS[activeTab].status);
  }, [activeTab, fetchBets]);

  const handleTabChange = (i) => {
    setActiveTab(i);
    setTickets([]);
  };

  return (
    <div className="mbt-page">
      <div className="mbt-page-header">
        <h1 className="mbt-page-title">🎯 Mes Paris</h1>
        <p className="mbt-page-subtitle">Retrouvez tous vos tickets de paris</p>
      </div>

      {/* Tabs */}
      <div className="mbt-tabs">
        {TABS.map((tab, i) => (
          <button
            key={tab.status}
            className={`mbt-tab ${activeTab === i ? 'active' : ''}`}
            onClick={() => handleTabChange(i)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && <TicketListSkeleton count={3} />}

      {/* Error */}
      {error && !loading && (
        <div className="mbt-error">⚠️ {error}</div>
      )}

      {/* Empty */}
      {!loading && !error && tickets.length === 0 && (
        <div className="mbt-empty">
          <span className="mbt-empty-icon">🎯</span>
          <p>Aucun pari {TABS[activeTab].label.split(' ').slice(1).join(' ').toLowerCase()} pour le moment.</p>
        </div>
      )}

      {/* Tickets list */}
      {!loading && !error && tickets.length > 0 && (
        <div className="mbt-tickets-list">
          {tickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} onSelect={setSelected} />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <TicketDetail ticket={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
